import type { ResolverTag } from '@ps/web-core-enums';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { camelCase, get } from 'lodash';
import type { ComponentClass, FunctionComponent } from 'react';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { getDisplayName } from '@swf/core/utils';

export interface InjectedResolverProps {
  resolverTags: { [tag: string]: string };
}

const withResolver =
  (tags: ResolverTag[]) =>
  (Comp: FunctionComponent<InjectedResolverProps> | ComponentClass<InjectedResolverProps>) => {
    const WithResolver: React.FC<InjectedResolverProps> = (props) => {
      const resolverTags = useSelector(select.resolver.get);
      const brand = useSelector(select.cms.getBrandName);
      const site = useSelector(select.cms.getSiteName);

      const {
        resolver: { fetchTag }
      } = useDispatch<Dispatch>();

      useEffect(() => tags.forEach((tag) => fetchTag({ tag })), [fetchTag, brand, site]);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return React.createElement<any>(
        Comp,
        {
          ...props,
          resolverTags: tags.reduce((memo: Record<string, ResolverTag>, tag: ResolverTag) => {
            memo[camelCase(tag)] = get(resolverTags, [tag, 'url'], '') as ResolverTag;
            return memo;
          }, {})
        },
        null
      );
    };

    WithResolver.displayName = `WithResolver(${getDisplayName(Comp as React.ComponentType)})`;
    hoistNonReactStatics(WithResolver, Comp);

    return WithResolver;
  };

export default withResolver;
